// The site's only client script — and until the "copy link" button it was
// empty: this file existed so Vite's CSS-via-JS-import mechanism produced the
// hashed stylesheet that scripts/prerender.mjs links from every page (see
// its cssLinks step). It is still that. The bundle it emits is linked only
// from the reflexión pages (see the jsTag step in the prerender); the home
// keeps it because Vite's shell does. Nothing hydrates, nothing renders.
import "./styles/global.css";
import "./styles/content.css";

// Press articles stay visible in static HTML. Controls appear only when the
// row overflows; touch, trackpad and keyboard scrolling also work without JS.
for (const carousel of document.querySelectorAll<HTMLElement>('[data-prensa]')) {
  const track = carousel.querySelector<HTMLElement>('.prensa__track');
  const controls = carousel.querySelector<HTMLElement>('.prensa__controls');
  const prev = carousel.querySelector<HTMLButtonElement>('[data-prev]');
  const next = carousel.querySelector<HTMLButtonElement>('[data-next]');
  const position = carousel.querySelector<HTMLElement>('[data-position]');
  if (!track || !controls || !prev || !next || !position) continue;
  const slides = [...track.querySelectorAll<HTMLElement>('.prensa__item')];
  if (!slides.length) continue;
  const offsets = () => slides.map(slide => Math.min(
    slide.offsetLeft - slides[0].offsetLeft, track.scrollWidth - track.clientWidth,
  ));
  const index = () => offsets().reduce((nearest, offset, i, all) =>
    Math.abs(offset - track.scrollLeft) < Math.abs(all[nearest] - track.scrollLeft) ? i : nearest, 0);
  const update = () => {
    controls.hidden = track.scrollWidth <= track.clientWidth + 1;
    prev.disabled = track.scrollLeft <= 1;
    next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
    position.textContent = `${index() + 1} de ${slides.length}`;
  };
  const move = (i: number) => track.scrollTo({
    left: offsets()[Math.max(0, Math.min(slides.length - 1, i))],
    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
  });
  prev.addEventListener('click', () => move(index() - 1));
  next.addEventListener('click', () => move(index() + 1));
  track.addEventListener('keydown', (event) => {
    if (event.target !== track) return;
    if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    move(event.key === 'Home' ? 0 : event.key === 'End' ? slides.length - 1 : index() + (event.key === 'ArrowRight' ? 1 : -1));
  });
  track.addEventListener('scroll', update, { passive: true });
  new ResizeObserver(update).observe(track);
  update();
}

// "Copiar el enlace" — hidden in the HTML, shown only when the clipboard is
// actually available, so a browser without it simply never offers it. Its
// two lines of feedback come from the button's data-* attributes, written by
// the prerender in the page's language — this script knows no Spanish.
for (const btn of document.querySelectorAll<HTMLButtonElement>("button[data-copiar]")) {
  if (!navigator.clipboard?.writeText) continue;
  btn.hidden = false;
  const label = btn.querySelector<HTMLElement>(".cp__copyLabel");
  const original = label?.textContent ?? "";
  const copiado = btn.dataset.copiado ?? "OK";
  const pedirCopia = btn.dataset.copiarPrompt ?? "";
  let timer = 0;
  btn.addEventListener("click", async () => {
    const enlace = btn.dataset.copiar ?? "";
    try {
      await navigator.clipboard.writeText(enlace);
    } catch {
      window.prompt(pedirCopia, enlace);
      return;
    }
    if (label) label.textContent = copiado;
    btn.classList.add("is-ok");
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      if (label) label.textContent = original;
      btn.classList.remove("is-ok");
    }, 2500);
  });
}

// Paragraph-level accompaniment. The published text remains readable without JS.
for (const oracion of document.querySelectorAll<HTMLElement>('.oracion')) {
  const audio = oracion.querySelector<HTMLAudioElement>('audio');
  if (!audio) continue;
  const parrafos = [...oracion.querySelectorAll<HTMLElement>('[data-inicio][data-fin]')];
  const seguir = oracion.querySelector<HTMLInputElement>('[data-seguir]');
  const error = oracion.querySelector<HTMLElement>('[data-error-reproduccion]');
  let activo: HTMLElement | undefined;
  const actualizar = (): void => {
    const wave = oracion.querySelector<HTMLElement>('.oracion__wave-progress');
    if (wave) wave.style.clipPath = `inset(0 ${100 - (Number.isFinite(audio.duration) && audio.duration > 0 ? Math.min(100, Math.max(0, audio.currentTime / audio.duration * 100)) : 0)}% 0 0)`;
    const siguiente = parrafos.find(p => audio.currentTime >= Number(p.dataset.inicio) && audio.currentTime < Number(p.dataset.fin));
    if (siguiente === activo) return;
    activo?.classList.remove('is-active');
    activo?.querySelector('button')?.removeAttribute('aria-current');
    activo = siguiente;
    activo?.classList.add('is-active');
    activo?.querySelector('button')?.setAttribute('aria-current', 'true');
    if (activo && seguir?.checked && !audio.paused) {
      activo.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }
  };
  for (const p of parrafos) {
    const button = p.querySelector<HTMLButtonElement>('button');
    if (!button) continue;
    button.disabled = false;
    button.addEventListener('click', () => {
      audio.currentTime = Number(p.dataset.inicio);
      actualizar();
      if (error) error.textContent = '';
      void audio.play().catch(() => { if (error) error.textContent = 'Pulse reproducir en el audio para continuar.'; });
    });
  }
  if (seguir?.parentElement) seguir.parentElement.hidden = false;
  const ayuda = oracion.querySelector<HTMLElement>('[data-ayuda]');
  if (ayuda) ayuda.hidden = false;
  audio.addEventListener('timeupdate', actualizar);
  audio.addEventListener('seeked', actualizar);
  audio.addEventListener('loadedmetadata', actualizar);
  audio.addEventListener('play', () => {
    for (const otro of document.querySelectorAll<HTMLAudioElement>('.oracion audio')) if (otro !== audio) otro.pause();
    actualizar();
  });
  audio.addEventListener('error', () => { if (error) error.textContent = 'No se pudo cargar el audio. Compruebe su conexión e inténtelo de nuevo.'; });
}

// Reader-controlled book endorsements; native horizontal scrolling works without JS.
for (const carousel of document.querySelectorAll<HTMLElement>('[data-testimonios]')) {
  const track = carousel.querySelector<HTMLElement>('.testimonios__track');
  const controls = carousel.querySelector<HTMLElement>('.testimonios__controls');
  const prev = carousel.querySelector<HTMLButtonElement>('[data-prev]');
  const next = carousel.querySelector<HTMLButtonElement>('[data-next]');
  const position = carousel.querySelector<HTMLElement>('[data-position]');
  if (!track || !controls || !prev || !next || !position) continue;
  const slides = [...track.querySelectorAll<HTMLElement>('.testimonios__item')];
  controls.hidden = false;
  const dots = [...carousel.querySelectorAll<HTMLButtonElement>('[data-slide]')];
  const index = () => Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / track.clientWidth)));
  const update = () => {
    const i = index();
    prev.disabled = i === 0;
    next.disabled = i === slides.length - 1;
    position.textContent = `${i + 1} de ${slides.length}`;
    dots.forEach((dot, n) => { if (n === i) dot.setAttribute("aria-current", "true"); else dot.removeAttribute("aria-current"); });
  };
  const move = (delta: number) => track.scrollTo({ left: Math.max(0, Math.min(slides.length - 1, index() + delta)) * track.clientWidth, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  dots.forEach((dot, i) => dot.addEventListener('click', () => move(i - index())));
  new ResizeObserver(update).observe(track);
  prev.addEventListener('click', () => move(-1));
  next.addEventListener('click', () => move(1));
  track.addEventListener('keydown', (event) => {
    if (event.target !== track) return;
    if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    move(event.key === 'Home' ? -index() : event.key === 'End' ? slides.length - 1 - index() : event.key === 'ArrowRight' ? 1 : -1);
  });
  track.addEventListener('scroll', update, { passive: true });
  update();
}
